import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { PROGRESS } from "@/constants/testIds";
import { toast } from "sonner";
import { Award, Flame, TrendingUp, Target } from "lucide-react";

export default function ProgressPage() {
  const [items, setItems] = useState([]);

  const load = () => api.get("/progress").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const overallLessons = items.reduce((a, b) => a + (b.completed_count || 0), 0);
  const overallQuizzes = items.reduce((a, b) => a + (b.quiz_scores?.length || 0), 0);
  const avgMastery = items.length ? Math.round(items.reduce((a, b) => a + (b.percent || 0), 0) / items.length) : 0;

  const claimCert = async (slug) => {
    try {
      await api.post(`/certificates/generate/${slug}`);
      toast.success("Certificate issued!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cannot issue yet");
    }
  };

  return (
    <div data-testid={PROGRESS.root} className="space-y-8 animate-fade-up">
      <div>
        <div className="label-mono text-primary">// progress</div>
        <h1 className="mt-2 font-display text-3xl font-black tracking-tighter sm:text-4xl">Your mastery.</h1>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        {[
          { l: "Avg. mastery", v: `${avgMastery}%`, icon: TrendingUp },
          { l: "Lessons completed", v: overallLessons, icon: Target },
          { l: "Quizzes taken", v: overallQuizzes, icon: Flame },
        ].map((s) => (
          <div key={s.l} className="card-hover rounded-2xl border border-border bg-card p-5">
            <div className="label-mono">{s.l}</div>
            <div className="mt-2 flex items-end justify-between">
              <div className="font-display text-4xl font-black tracking-tighter">{s.v}</div>
              <s.icon className="h-6 w-6 text-primary" />
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-border bg-card p-6">
        <div className="label-mono mb-4">// paths</div>
        {items.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-6 text-center">
            <div className="text-sm text-muted-foreground">No progress yet — enroll in a path to get started.</div>
            <Link to="/app/paths" className="mt-3 inline-block text-xs font-mono text-primary hover:underline">Browse paths →</Link>
          </div>
        ) : (
          <div className="space-y-4">
            {items.map((p) => (
              <div key={p.path_slug} data-testid={PROGRESS.pathRow(p.path_slug)} className="rounded-xl border border-border p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className="grid h-10 w-10 place-items-center rounded-lg font-mono text-xs"
                      style={{ backgroundColor: `${p.path_color}22`, color: p.path_color, border: `1px solid ${p.path_color}55` }}
                    >
                      {p.path_title[0]}
                    </div>
                    <div className="min-w-0">
                      <div className="font-display font-bold">{p.path_title}</div>
                      <div className="label-mono">{p.completed_count} / {p.total_lessons} lessons · {p.percent}% mastery</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="label-mono">Quiz avg: {p.avg_quiz_percent || 0}%</div>
                    {p.percent === 100 && (
                      <Button
                        data-testid={PROGRESS.generateCertBtn(p.path_slug)}
                        onClick={() => claimCert(p.path_slug)}
                        variant="outline"
                        size="sm"
                        className="rounded-full font-mono text-xs uppercase tracking-widest"
                      >
                        <Award className="mr-1.5 h-3.5 w-3.5" /> Claim
                      </Button>
                    )}
                  </div>
                </div>
                <Progress value={p.percent} className="mt-3 h-1.5" />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
